import { useEffect, useRef, useState } from "react";
import { Alert, Button, Input, Spin } from "antd";
import { ApiError, exchangeCrmSession, loginWithCrm, setToken } from "../api";
import "./login.css";

/** Вход через CRM: страница CRM берёт SSO-токен и возвращает на sms.brasol.ru/#sso=… */
const CRM_SSO_URL = "https://crm.brasol.ru/marketing/rfm-center";
const CRM_HOME_URL = "https://crm.brasol.ru/";

/**
 * Вход как в Базе знаний: логин и пароль CRM. Session-cookie CRM живёт на
 * .brasol.ru, поэтому если юзер уже залогинен в CRM — входим молча без
 * формы (silent SSO). Логин идёт через прокси /api/crm/login → CRM.
 * Визуал — экран «Вход через CRM» прототипа «SMS-центр» (две колонки, кнопка
 * «Войти через CRM БРАСОЛ»); вход по почте и паролю и сервисный токен сохранены,
 * логика и тексты ошибок (api.auth.test.ts) без изменений.
 */
export default function LoginScreen({
  error,
  onToken,
}: {
  error: string | null;
  onToken: () => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [manual, setManual] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [busyForm, setBusyForm] = useState(false);
  const [silentTried, setSilentTried] = useState(!!error);
  const silentRequest = useRef<Promise<{ name: string }> | null>(null);
  const submitInFlight = useRef(false);

  // Уже залогинен в CRM (cookie .brasol.ru) → входим сами.
  useEffect(() => {
    if (error) {
      setSilentTried(true);
      return;
    }
    let active = true;
    // Не запускаем второй обмен одноразовой ссылки при повторной подписке StrictMode.
    silentRequest.current ??= exchangeCrmSession();
    silentRequest.current
      .then(() => {
        if (active) onToken();
      })
      .catch((cause: unknown) => {
        if (
          active &&
          !(cause instanceof ApiError && [401, 403, 409].includes(cause.status))
        )
          setFormError(
            cause instanceof Error
              ? cause.message
              : "Не удалось выполнить автоматический вход. Войдите с логином и паролем.",
          );
      })
      .finally(() => {
        if (active) setSilentTried(true);
      });
    return () => {
      active = false;
    };
  }, [error, onToken]);

  const submit = async () => {
    if (submitInFlight.current || !email || !password) return;
    submitInFlight.current = true;
    setBusyForm(true);
    setFormError(null);
    try {
      await loginWithCrm(email, password);
      onToken();
    } catch (cause) {
      setFormError(
        cause instanceof Error
          ? cause.message
          : "Не удалось войти. Попробуйте ещё раз.",
      );
    } finally {
      submitInFlight.current = false;
      setBusyForm(false);
    }
  };

  return (
    <div className="lg-root">
      <div className="lg-card">
        <div className="lg-main">
          <img
            className="lg-logo"
            src="/brand/fishka-logo.png"
            alt="fishka — суши, роллы"
          />
          <div>
            <h1 className="lg-title">SMS-центр</h1>
            <div className="lg-sub">
              CRM-маркетинг сети fishka · sms.brasol.ru
            </div>
          </div>
          {!silentTried ? (
            <Spin spinning>
              <div className="lg-silent" role="status" aria-live="polite">
                Проверяем сессию CRM…
              </div>
            </Spin>
          ) : (
            <>
              <p className="lg-text">
                Вход выполняется через учётную запись CRM БРАСОЛ — отдельный
                пароль не нужен. После входа откроется кабинет той роли, что
                назначена в CRM: управляющая компания или партнёр.
              </p>
              {error && <Alert type="warning" message={error} />}
              {formError && <Alert type="error" message={formError} />}
              <div className="lg-actions">
                {/* CRM выдаёт одноразовую ссылку и возвращает сюда с #sso=… (RfmCenterRedirectPage). */}
                <a className="lg-crm-btn" href={CRM_SSO_URL}>
                  Войти через CRM БРАСОЛ →
                </a>
                <a className="lg-open-crm" href={CRM_HOME_URL}>
                  Открыть CRM
                </a>
              </div>
              <div className="lg-divider">
                <span>или почта и пароль от CRM</span>
              </div>
              <form
                className="lg-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void submit();
                }}
              >
                <Input
                  placeholder="E-mail"
                  type="email"
                  name="username"
                  autoComplete="username"
                  aria-label="E-mail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Input.Password
                  placeholder="Пароль"
                  name="password"
                  autoComplete="current-password"
                  aria-label="Пароль"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  disabled={!email || !password}
                  loading={busyForm}
                >
                  Войти
                </Button>
              </form>
              <details className="lg-token">
                <summary>Сервисный токен</summary>
                <div className="lg-token-row">
                  <Input.Password
                    placeholder="Токен из настроек CRM"
                    aria-label="Сервисный токен"
                    value={manual}
                    onChange={(e) => setManual(e.target.value)}
                  />
                  <Button
                    disabled={!manual.trim() || busyForm}
                    onClick={() => {
                      setToken(manual.trim());
                      onToken();
                    }}
                  >
                    Войти по токену
                  </Button>
                </div>
              </details>
            </>
          )}
          <p className="lg-note">
            Уже вошли в CRM? Кабинет откроется автоматически. Пароль меняется в
            CRM БРАСОЛ.
          </p>
          <div className="lg-foot">
            © BRASOL · Retention-CRM · общий аккаунт SMSC для всей сети
          </div>
        </div>
        <aside className="lg-side" aria-hidden="true">
          <img src="/brand/login-shrimp.png" alt="" />
          <div className="lg-side-title">
            Кампании, которые возвращают гостей
          </div>
          <div className="lg-side-text">
            Пуши, письма и SMS по сценарию; заказы после сообщений — в одном
            отчёте с расходами.
          </div>
        </aside>
      </div>
    </div>
  );
}
